import { Fragment, useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { ChevronDown, ChevronLeft, ChevronRight, CircleCheck, Import, RefreshCw, TriangleAlert } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, hasPermission, IMPORT_KIND_LABELS, IMPORT_SOURCE_LABELS, IMPORT_STAT_LABELS, labelOf } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Label,
  PageHeader,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { useListState } from '../../lib/list-search.js';

/** Statuses the backend writes (import_runs.status). */
const STATUS_OPTIONS = [
  { value: 'queued', label: 'Wartend' },
  { value: 'processing', label: 'Läuft' },
  { value: 'completed', label: 'Erfolgreich' },
  { value: 'failed', label: 'Fehlgeschlagen' },
];
const toOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

/** Administration › Importe: deliveries of the accounting sync agent (WinCasa) and Cobra. */
export function ImportRunsPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = { ...list.apiParams, filter: { source: search.source, status: search.status } };
  const runs = useQuery(adminQueries.importRuns(api, params));
  const { data: me } = useMe();
  const [syncOpen, setSyncOpen] = useState(false);
  const canSync = hasPermission(me, 'integration.manage');

  return (
    <>
      <PageHeader
        title="Importe"
        description="Lieferungen aus WinCasa (Buchhaltung), Cobra und dem Heizkosten-Postfach – Verarbeitung, Zähler, Warnungen und Fehler."
        actions={
          canSync && (
            <Button size="sm" variant="outline" onClick={() => setSyncOpen(true)}>
              <RefreshCw aria-hidden="true" /> Abgleich starten
            </Button>
          )
        }
      />
      {canSync && <StartSyncDialog open={syncOpen} onOpenChange={setSyncOpen} />}
      <HealthBanner />
      <FilterBar>
        <FilterSelect label="Quelle" value={search.source} onChange={(v) => list.setFilter('source', v)} options={toOptions(IMPORT_SOURCE_LABELS)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
      </FilterBar>
      {runs.error ? (
        <ErrorState error={runs.error} onRetry={() => runs.refetch()} />
      ) : runs.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : runs.data.data.length === 0 ? (
        <EmptyState icon={Import} title="Keine Importläufe" description="Für die gewählten Filter wurden noch keine Lieferungen empfangen." />
      ) : (
        <div className={runs.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
          <RunsTable runs={runs.data.data} />
          <Pager meta={runs.data.meta} pagination={list.pagination} onChange={list.onPaginationChange} />
        </div>
      )}
    </>
  );
}

/** Label of a `blimo:sync` target (unknown targets are shown raw). @param {{ target: string, source: string, group: boolean }} t */
function syncTargetLabel(t) {
  if (t.target === 'all') return `${labelOf(IMPORT_SOURCE_LABELS, 'accounting')} – alles`;
  if (t.target === 'cobra') return 'Cobra – alles';
  const kind = t.source === 'cobra' ? t.target.replace(/^cobra-/, '') : t.target;
  const kindLabel = t.source === 'cobra' ? (COBRA_KIND_LABELS[kind] ?? kind) : labelOf(IMPORT_KIND_LABELS, kind);
  return `${t.source === 'cobra' ? 'Cobra' : 'WinCasa'} · ${kindLabel}`;
}

const COBRA_KIND_LABELS = { keys: 'Schlüssel (Liegenschaften/Einheiten)', persons: 'Personen', meters: 'Zähler', tickets: 'Vorgänge' };

/** F-316: start a sync like `blimo:sync <target>` (`integration.manage`); the server rejects duplicates with 409. */
function StartSyncDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{open && <StartSyncBody onClose={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function StartSyncBody({ onClose }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const targets = useQuery(adminQueries.importSyncTargets(api));
  const [target, setTarget] = useState('all');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const selectId = useId();
  const selected = (targets.data ?? []).find((t) => t.target === target);

  async function start() {
    setPending(true);
    setError(null);
    try {
      await api.admin.importRuns.start(target);
      toast.success('Abgleich gestartet – die Import-Läufe erscheinen nach dem Einlesen in der Liste.');
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('import-runs') });
      onClose();
    } catch (e) {
      setError(getErrorMessage(e, 'Der Abgleich konnte nicht gestartet werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Abgleich starten</DialogTitle>
        <DialogDescription>
          Liest die gewählten Daten aus WinCasa bzw. Cobra und verarbeitet sie wie der nächtliche Abgleich. Je Quelle läuft höchstens ein Abgleich gleichzeitig.
        </DialogDescription>
      </DialogHeader>
      {targets.error ? (
        <ErrorState compact error={targets.error} onRetry={() => targets.refetch()} />
      ) : targets.isPending ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={selectId}>Umfang</Label>
          <Select id={selectId} value={target} onChange={(e) => setTarget(e.target.value)}>
            {targets.data.map((t) => (
              <option key={t.target} value={t.target}>
                {syncTargetLabel(t)}
                {t.busy ? ' (Import läuft)' : ''}
              </option>
            ))}
          </Select>
          {selected?.busy && <p className="text-xs text-muted-foreground">Für diese Quelle läuft gerade ein Import – bitte das Ende abwarten.</p>}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Abbrechen
        </Button>
        <Button type="button" onClick={start} loading={pending} disabled={!selected || selected.busy}>
          Abgleich starten
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Stale feeds (no successful delivery for > 26 h, decided by the API) as a warning on top. */
function HealthBanner() {
  const api = useApi();
  const health = useQuery(adminQueries.importRunsHealth(api));
  if (health.isPending || health.error) return null;
  const feeds = health.data ?? [];
  const stale = feeds.filter((f) => f.stale);
  const feedName = (f) => `${labelOf(IMPORT_SOURCE_LABELS, f.source)} · ${labelOf(IMPORT_KIND_LABELS, f.kind)}`;
  if (stale.length > 0) {
    return (
      <Alert variant="warning" role="alert" className="mb-3">
        <TriangleAlert aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <AlertTitle>Veraltete Lieferungen</AlertTitle>
          <AlertDescription>
            <p>Seit mehr als 26 Stunden keine erfolgreiche Lieferung – bitte den Sync-Agenten prüfen.</p>
            <ul className="mt-1 flex flex-col gap-0.5" aria-label="Veraltete Lieferungen">
              {stale.map((f) => (
                <li key={`${f.source}-${f.kind}`}>
                  <span className="font-medium">{feedName(f)}</span>: letzte erfolgreiche Lieferung{' '}
                  {f.last_success ? formatDateTime(f.last_success) : 'noch nie'}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </div>
      </Alert>
    );
  }
  if (feeds.length === 0) return null;
  return (
    <Alert variant="success" className="mb-3">
      <CircleCheck aria-hidden="true" />
      <AlertDescription>Alle Lieferungen aktuell ({feeds.map((f) => `${feedName(f)}: ${formatDateTime(f.last_success)}`).join(' · ')}).</AlertDescription>
    </Alert>
  );
}

function RunsTable({ runs }) {
  return (
    <Table aria-label="Importläufe" containerClassName="rounded-md border">
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">Details</span>
          </TableHead>
          <TableHead>Empfangen</TableHead>
          <TableHead>Quelle</TableHead>
          <TableHead>Art</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Beendet</TableHead>
          <TableHead data-numeric="true">Größe</TableHead>
          <TableHead>Hinweise</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </TableBody>
    </Table>
  );
}

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const warnings = run.warnings ?? [];
  const hasDetails = Boolean(run.error) || warnings.length > 0 || (run.stats && Object.keys(run.stats).length > 0);
  return (
    <Fragment>
      <TableRow>
        <TableCell className="w-8">
          {hasDetails && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-expanded={open}
              aria-controls={detailId}
              aria-label={`Details zu Lauf #${run.id}`}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
            </Button>
          )}
        </TableCell>
        <TableCell className="numeric">{formatDateTime(run.created_at)}</TableCell>
        <TableCell>{labelOf(IMPORT_SOURCE_LABELS, run.source)}</TableCell>
        <TableCell>{labelOf(IMPORT_KIND_LABELS, run.kind)}</TableCell>
        <TableCell>
          <StatusBadge domain="importRun" status={run.status} />
        </TableCell>
        <TableCell className="numeric">{formatDateTime(run.finished_at)}</TableCell>
        <TableCell data-numeric="true">
          {run.payload_bytes == null ? '–' : `${formatNumber(run.payload_bytes / 1024, { maximumFractionDigits: 1 })} KB`}
        </TableCell>
        <TableCell className="text-xs">
          {run.error ? (
            <span className="text-destructive">Fehler</span>
          ) : warnings.length > 0 ? (
            <span className="text-warning-subtle-foreground">{formatNumber(run.stats?.warnings ?? warnings.length)} Warnungen</span>
          ) : (
            <span className="text-muted-foreground">–</span>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow id={detailId} className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={8} className="py-3">
            <RunDetails run={run} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function RunDetails({ run }) {
  const stats = Object.entries(run.stats ?? {});
  const warnings = run.warnings ?? [];
  return (
    <div className="grid gap-4 text-sm lg:grid-cols-[minmax(12rem,20rem)_1fr]">
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold">Zähler</h3>
        {stats.length === 0 ? (
          <p className="text-muted-foreground">Keine Zähler.</p>
        ) : (
          <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
            {stats.map(([key, value]) => (
              <Fragment key={key}>
                <dt className="text-muted-foreground">{labelOf(IMPORT_STAT_LABELS, key)}</dt>
                <dd className="numeric text-right">{typeof value === 'number' ? formatNumber(value) : String(value)}</dd>
              </Fragment>
            ))}
          </dl>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Lauf #{run.id}
          {run.token_name ? ` · Token „${run.token_name}“` : ''}
          {run.started_at ? ` · gestartet ${formatDateTime(run.started_at)}` : ''}
        </p>
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        {run.error && (
          <div className="flex flex-col gap-1">
            <h3 className="font-semibold text-destructive">Fehler</h3>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-card p-2 text-xs">{run.error}</pre>
          </div>
        )}
        {warnings.length > 0 && (
          <div className="flex flex-col gap-1">
            <h3 className="font-semibold">Warnungen {warnings.length >= 20 && <span className="font-normal text-muted-foreground">(die ersten 20)</span>}</h3>
            <ul className="max-h-48 list-disc overflow-auto pl-5 text-xs" aria-label={`Warnungen Lauf #${run.id}`}>
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Pager({ meta, pagination, onChange }) {
  const total = meta?.total ?? 0;
  const lastPage = Math.max(1, meta?.last_page ?? 1);
  const page = pagination.pageIndex + 1;
  if (lastPage <= 1) return <p className="mt-2 text-xs text-muted-foreground">{formatNumber(total)} Läufe</p>;
  return (
    <div className="mt-2 flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">
        Seite {page} von {lastPage} · {formatNumber(total)} Läufe
      </span>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
          aria-label="Vorherige Seite"
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= lastPage}
          onClick={() => onChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
          aria-label="Nächste Seite"
        >
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
